import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '@/hooks/useAuth'
import LanguagePicker from '@/components/ui/LanguagePicker'
import { ChevronDown, Sparkles, DollarSign } from 'lucide-react'

const IN = '#0ea5e9'
const G = '#d4a843'
const BR = 'rgba(255,255,255,0.08)'
const MU = 'rgba(255,255,255,0.45)'
const HERO_PATHS = ['/university/', '/university/corporate']

function NavDropdown({ label, items }: { label: string, items: { label: string, path: string, desc?: string }[] }) {
  const [isOpen, setIsOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  return (
    <div
      style={{ position: 'relative' }}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button style={{
        background: 'none', border: 'none', cursor: 'pointer', padding: '10px 16px', borderRadius: 10,
        fontSize: 14, fontWeight: 500, color: isOpen ? '#fff' : 'rgba(255,255,255,0.7)',
        display: 'flex', alignItems: 'center', gap: 6, transition: 'all 0.2s'
      }}>
        {label}
        <ChevronDown size={14} style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', opacity: 0.5 }} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            style={{
              position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
              marginTop: 8, width: 240, background: 'rgba(20,20,35,0.98)',
              backdropFilter: 'blur(20px)', border: `1px solid ${BR}`, borderRadius: 16,
              boxShadow: '0 20px 40px rgba(0,0,0,0.4)', padding: 10, zIndex: 1000
            }}
          >
            {items.map(item => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                style={{
                  width: '100%', textAlign: 'left', background: location.pathname === item.path ? 'rgba(212,168,67,0.08)' : 'none',
                  border: 'none', borderRadius: 10, padding: '12px 14px', cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
                onMouseLeave={e => e.currentTarget.style.background = location.pathname === item.path ? 'rgba(212,168,67,0.08)' : 'none'}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: location.pathname === item.path ? G : '#fff', marginBottom: 2 }}>
                  {item.label}
                </div>
                {item.desc && <div style={{ fontSize: 11, color: MU, lineHeight: 1.4 }}>{item.desc}</div>}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default function UniversityHeader() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { user, userProfile, signOut } = useAuth()
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024)

  const isDashboard = location.pathname === '/university/dashboard'
  const isMobileOrDashboard = isMobile || isDashboard

  const transparent = HERO_PATHS.includes(location.pathname) && !scrolled

  // Use a simple state for width-based logic without constant state updates if possible, 
  // but for the sake of reactivity we'll keep the resize listener.
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024)
    check(); window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', fn, { passive: true })
    return () => window.removeEventListener('scroll', fn)
  }, [])

  useEffect(() => { setOpen(false) }, [location.pathname])

  const studyItems = [
    { label: t('university.prep.eyebrow', 'Prep'), path: '/prep' },
    { label: t('university.footer.links.allProgrammes', 'All Programmes'), path: '/university/enrol' },
    ...(user ? [{ label: t('university.nav.dashboard', 'My Dashboard'), path: '/university/dashboard' }] : []),
    ...(user ? [{ label: t('university.nav.settings', 'Account Settings'), path: '/settings' }] : []),
    { label: t('university.nav.progress', 'Personal Progress'), path: '/university/progress' },
    { label: t('university.nav.certificates', 'Certificates'), path: '/university/certificates' },
  ]

  const impactItems = [
    { label: t('university.footer.links.giftDegree', 'Gift a Degree'), path: '/university/gift', desc: 'Buy education for someone' },
    { label: t('university.footer.links.payForward', 'Pay It Forward'), path: '/university/give', desc: 'Fund scholarships for others' },
    { label: t('university.redeem.title', 'Claim a Gift'), path: '/university/redeem' },
  ]

  const orgItems = [
    { label: t('university.footer.links.corporatePlans', 'Corporate Plans'), path: '/university/corporate', desc: 'Upskill your entire team' },
    { label: t('university.footer.links.nfpProgramme', 'NFP Programme'), path: '/university/nfp', desc: 'Subsidised NGO access' },
    ...(user ? [{ label: t('university.footer.links.corporateAdmin', 'Corporate Admin'), path: '/university/corporate/admin' }] : []),
  ]

  return (
    <>
      <header style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: transparent ? 'rgba(6,6,12,0)' : 'rgba(6,6,12,0.92)',
        borderBottom: `1px solid ${transparent ? 'transparent' : BR}`,
        backdropFilter: transparent ? 'none' : 'blur(20px)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: isMobile ? '0 16px' : '0 24px', height: isMobile ? 64 : 72, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

          {/* Logo & Section Groups */}
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 32, flexShrink: 0 }}>
            <button
              onClick={() => navigate('/')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 14, padding: 0 }}
            >
              <div style={{ width: isMobile ? 44 : 56, height: isMobile ? 44 : 56, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <img
                  src="/icons/blue-sage-logo.png"
                  alt="Still Waters"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </div>
              <span style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontWeight: 900,
                fontSize: isMobile ? 20 : 26,
                color: '#ffffff',
                letterSpacing: '-0.03em',
                whiteSpace: 'nowrap',
              }}>
                Still Waters
              </span>
            </button>

            {!isMobileOrDashboard && (
              <nav style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <NavDropdown label={t('university.footer.headings.study', 'Study')} items={studyItems} />
                <NavDropdown label={t('university.footer.headings.organisations', 'Organisations')} items={orgItems} />
                <NavDropdown label={t('university.footer.headings.give', 'Impact')} items={impactItems} />
              </nav>
            )}
          </div>

          {/* Right side Actions */}
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            {!isMobileOrDashboard && <LanguagePicker />}

            {!isMobileOrDashboard && (
              user ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {userProfile?.active_plan?.includes('Scholarship') && (
                    <div style={{
                      background: 'rgba(14,165,233,0.1)',
                      border: '1px solid rgba(14,165,233,0.3)',
                      borderRadius: 8, padding: '4px 10px',
                      fontSize: 11, fontWeight: 700, color: IN,
                      display: 'flex', alignItems: 'center', gap: 5
                    }}>
                      <Sparkles size={12} /> {t('university.nav.scholarship', 'Scholarship Student')}
                    </div>
                  )}

                  {userProfile?.is_affiliate && (
                    <button
                      onClick={() => window.open('https://stillwatersacademy.com/affiliates/dashboard', '_blank')}
                      style={{
                        background: 'rgba(212,168,67,0.1)',
                        border: `1px solid ${G}40`,
                        color: G, borderRadius: 12, padding: '10px 18px',
                        fontSize: 13, fontWeight: 700, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 6,
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(212,168,67,0.15)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(212,168,67,0.1)'}
                    >
                      <DollarSign size={14} /> {t('university.nav.affiliateDashboard', 'Affiliate Dashboard')}
                    </button>
                  )}
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate('/university/dashboard')}
                    style={{ background: `linear-gradient(135deg, ${G}, #b8912e)`, color: '#06060c', border: 'none', borderRadius: 12, padding: '10px 20px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 15px rgba(212,168,67,0.2)' }}
                  >
                    {t('university.nav.dashboard', 'Dashboard')}
                  </motion.button>
                  <button
                    onClick={() => signOut()}
                    style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${BR}`, color: 'rgba(255,255,255,0.45)', borderRadius: 12, padding: '10px 18px', fontSize: 13, fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#fff'}
                    onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.45)'}
                  >
                    {t('university.nav.signOut', 'Sign Out')}
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    onClick={() => navigate('/signin')}
                    style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', padding: '10px 18px', fontSize: 14, fontWeight: 500, cursor: 'pointer', transition: 'color 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.color = '#fff'}
                    onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}
                  >
                    {t('university.nav.signIn', 'Sign In')}
                  </button>
                  <motion.button
                    whileHover={{ scale: 1.04, boxShadow: '0 8px 24px rgba(212,168,67,0.3)' }}
                    whileTap={{ scale: 0.96 }}
                    onClick={() => navigate('/university/enrol')}
                    style={{ background: `linear-gradient(135deg, ${G}, #b8912e)`, color: '#06060c', border: 'none', borderRadius: 14, padding: '11px 24px', fontSize: 14, fontWeight: 800, cursor: 'pointer' }}
                  >
                    {t('university.nav.enrol', 'Enrol Now')}
                  </motion.button>
                </div>
              )
            )}

            {isMobileOrDashboard && (
              <button
                onClick={() => setOpen(o => !o)}
                style={{
                  background: 'rgba(255,255,255,0.03)', border: `1px solid ${BR}`, borderRadius: 12, width: 44, height: 44, cursor: 'pointer', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
              >
                {open ? '✕' : '☰'}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              style={{ position: 'fixed', inset: 0, zIndex: 98, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }}
            />
            <motion.div
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '85%', maxWidth: 360, zIndex: 99, background: '#06060c', borderLeft: `1px solid ${BR}`, padding: '80px 24px 40px', display: 'flex', flexDirection: 'column' }}
            >
              <div style={{ marginBottom: 32 }}>
                <LanguagePicker />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
                <div>
                  <p style={{ fontSize: 10, fontWeight: 800, color: G, letterSpacing: '2px', marginBottom: 16 }}>STUDY</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {studyItems.map(l => (
                      <button key={l.path} onClick={() => navigate(l.path)} style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, fontWeight: 700, color: location.pathname === l.path ? G : '#fff' }}>
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p style={{ fontSize: 10, fontWeight: 800, color: G, letterSpacing: '2px', marginBottom: 16 }}>IMPACT</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {impactItems.map(l => (
                      <button key={l.path} onClick={() => navigate(l.path)} style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, fontWeight: 700, color: location.pathname === l.path ? G : '#fff' }}>
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p style={{ fontSize: 10, fontWeight: 800, color: G, letterSpacing: '2px', marginBottom: 16 }}>ORGANISATIONS</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {orgItems.map(l => (
                      <button key={l.path} onClick={() => navigate(l.path)} style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, fontWeight: 700, color: location.pathname === l.path ? G : '#fff' }}>
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
                {user ? (
                  <button onClick={() => signOut()} style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${BR}`, color: MU, borderRadius: 16, padding: '16px', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
                    {t('university.nav.signOut', 'Sign Out')}
                  </button>
                ) : (
                  <>
                    <button onClick={() => navigate('/signin')} style={{ background: 'none', border: `1px solid ${BR}`, color: 'rgba(255,255,255,0.8)', borderRadius: 16, padding: '16px', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
                      {t('university.nav.signIn', 'Sign In')}
                    </button>
                    <button onClick={() => navigate('/university/enrol')} style={{ background: `linear-gradient(135deg, ${G}, #b8912e)`, color: '#0d0d1a', border: 'none', borderRadius: 16, padding: '16px', fontSize: 16, fontWeight: 800, cursor: 'pointer' }}>
                      {t('university.nav.enrol', 'Enrol Now')}
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      <div style={{ height: isMobile ? 64 : 72 }} />
    </>
  )
}
